import { z } from 'zod'
import { DiditModeSchema, ElectionStatusSchema, MemberStatusSchema, OrganizationStatusSchema } from '../enums'
import { DistrictEntrySchema, DistrictsSchema } from '../domain/election-config'
import {
  MODULE_IDS,
  ModuleIdSchema,
  REQUIREMENTS_WARNING_CODES,
  RequirementsSchema,
  RequirementsV2Schema,
  requirementsIssues,
} from '../domain/requirements'
import { TestMixSchema, TestOutcomeSchema } from '../domain/test-mode'
import { EthAddressSchema, IsoDateTimeSchema, SlugSchema, UuidSchema } from '../primitives'
import { DiditRejectionSchema } from './common'
import { EmailStatusSchema } from './communications'
import { ElectionSummarySchema } from './elections'

/**
 * Organizations (PLAN v3 §1, §4): an organizer owns one or more organizations; an organization owns its member
 * community (registration rules, KYC, districts, test mode) and creates votings ("elections") for its members.
 * Organizer routes: `/api/org/organizations[/:orgId]` (cookie `org_session`, owner only: another organizer's
 * organization answers 404).
 */

/* ================================================================== body */

/**
 * Everything the organizer authors (new-organization wizard: basics → registration rules → review & publish).
 * Drafts are saved with lenient validation; `organizationPublishIssues()` lists what still blocks publishing (the
 * server runs the same function on POST /publish and answers 400 validation_error with those issues).
 */
export const OrganizationBodySchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(200),
  /**
   * Public address `/o/:slug`. Omitted ⇒ generated from the name. Unique (409 slug_taken). Fixed once published
   * (409 organization_not_editable `fields: ['slug']`): join links and invitations point to it.
   */
  slug: SlugSchema.optional(),
  description: z.string().trim().max(10_000).default(''),
  /**
   * Membership requirements (v2 modules, PLAN v4 §1.1). A v1-shaped object (`{countries, phone, minAge, …}`) is still
   * accepted and upgraded (compatibility).
   */
  requirements: RequirementsSchema.prefault({}),
  /** Districting list `locality,district`. Votings may restrict eligibility to some districts. */
  districts: DistrictsSchema,
  /**
   * Test organization (M5): identity verification through the Didit SANDBOX app, codes always accepted, "[TEST]" on
   * chain for every voting, emails rewritten to the test sink (M4). Fixed once published (409
   * organization_not_editable `fields: ['testMode']`).
   */
  testMode: z.boolean().default(false),
  /** Test organizations only: how each member's sandbox verification ends (see domain/test-mode.ts). */
  testOutcome: TestOutcomeSchema.default('random'),
  /** Test organizations with `random` outcome: share of approved / in review / declined (sums to 100). */
  testMix: TestMixSchema.prefault({}),
})
export type OrganizationBody = z.infer<typeof OrganizationBodySchema>
export type OrganizationBodyDraft = z.input<typeof OrganizationBodySchema>

/* POST /api/org/organizations → 201 OrganizationResponse (status draft). 409 slug_taken. */
export const OrganizationCreateRequestSchema = OrganizationBodySchema
export type OrganizationCreateRequest = z.input<typeof OrganizationCreateRequestSchema>

/**
 * What happens to EXISTING members when a requirements change makes some of them non-compliant (PLAN v4 R6):
 * - keep (default): they stay eligible; the non-compliant modules are recorded as per-member exemptions;
 * - reset: non-compliant members are reset (→ revoked, they join and verify again; their census slot is kept, R1).
 * Area / citizenship-list changes need no new proof (re-evaluated live) but still count as non-compliance.
 */
export const EXISTING_MEMBERS_POLICIES = ['keep', 'reset'] as const
export const ExistingMembersPolicySchema = z.enum(EXISTING_MEMBERS_POLICIES)
export type ExistingMembersPolicy = z.infer<typeof ExistingMembersPolicySchema>

/**
 * PATCH /api/org/organizations/:orgId → OrganizationResponse. Any subset; arrays replace the stored value.
 * After publishing: `slug` and `testMode` are locked (409 organization_not_editable, details.fields); requirement
 * and district changes only affect future registrations (the Didit workflow is ensured / switched first: 422
 * didit_config_rejected / 503 didit_unavailable and nothing is saved). `existingMembers` applies to a requirements
 * change only (default keep; preview the impact with POST …/requirements/preview).
 */
export const OrganizationUpdateRequestSchema = z
  .object({
    name: OrganizationBodySchema.shape.name,
    slug: SlugSchema,
    description: z.string().trim().max(10_000),
    requirements: RequirementsSchema,
    // No `.default([])` here: an omitted field must stay omitted (a default would wipe the stored list).
    districts: z.array(DistrictEntrySchema).max(20_000),
    testMode: z.boolean(),
    testOutcome: TestOutcomeSchema,
    testMix: TestMixSchema,
    existingMembers: ExistingMembersPolicySchema,
  })
  .partial()
export type OrganizationUpdateRequest = z.input<typeof OrganizationUpdateRequestSchema>

/*
 * POST /api/org/organizations/:orgId/requirements/preview → RequirementsChangePreviewResponse
 * Impact of replacing the requirements, computed with moduleCompliance() over the organization's ACTIVE members
 * (nothing is saved). The settings dialog offers keep / reset when `nonCompliant.total > 0`.
 */
export const RequirementsChangePreviewRequestSchema = z.object({ requirements: RequirementsSchema })
export type RequirementsChangePreviewRequest = z.input<typeof RequirementsChangePreviewRequestSchema>

export const RequirementsWarningSchema = z.object({
  code: z.enum(REQUIREMENTS_WARNING_CODES),
  path: z.array(z.union([z.string(), z.number()])),
  message: z.string(),
})

export const RequirementsChangePreviewResponseSchema = z.object({
  /** Active members (the population checked). */
  activeMembers: z.number().int().min(0),
  /** Active members that would NOT comply with the new requirements (exemptions already held are honoured). */
  nonCompliant: z.object({
    total: z.number().int().min(0),
    /** Per module (a member can count in several). Every module key present. */
    byModule: z.record(ModuleIdSchema, z.number().int().min(0)),
  }),
  /**
   * What "Reset non-compliant members" would reset: the non-compliant members EXCLUDING removed ones (a reset never
   * touches removed members; keep exempts them too, so `nonCompliant` counts them). Every module key present.
   */
  resettable: z.object({
    total: z.number().int().min(0),
    byModule: z.record(ModuleIdSchema, z.number().int().min(0)),
  }),
  /** Modules newly enabled or narrowed by the change. */
  stricterModules: z.array(ModuleIdSchema),
  /** Non-blocking warnings of the new requirements (requirementsWarnings, R15). */
  warnings: z.array(RequirementsWarningSchema),
})
export type RequirementsChangePreviewResponse = z.infer<typeof RequirementsChangePreviewResponseSchema>

/** Zero counts for every module (preview responses, mocks). */
export const emptyModuleCounts = (): Record<(typeof MODULE_IDS)[number], number> =>
  Object.fromEntries(MODULE_IDS.map((m) => [m, 0])) as Record<(typeof MODULE_IDS)[number], number>

export interface OrganizationPublishIssue {
  path: (string | number)[]
  message: string
}

/**
 * What still blocks publishing an organization. Empty array ⇒ publishable. PLAN v4: the v1 "≥1 country" rule is
 * gone (an identity document of any country is fine without the citizenship module); the issues are the
 * requirements consistency rules (`requirementsIssues`, the same superRefine as RequirementsV2Schema).
 */
export function organizationPublishIssues(body: OrganizationBody): OrganizationPublishIssue[] {
  return requirementsIssues(body.requirements).map((i) => ({ path: ['requirements', ...i.path], message: i.message }))
}

/* ================================================================== stored organization */

export const OrganizationActionsSchema = z.object({
  canEdit: z.boolean(),
  canPublish: z.boolean(),
  /** Drafts without votings only. */
  canDelete: z.boolean(),
  /** Votings can be drafted any time; publishing / starting them needs a published organization. */
  canCreateElection: z.boolean(),
})
export type OrganizationActions = z.infer<typeof OrganizationActionsSchema>

export const OrganizationSchema = z.object({
  id: UuidSchema,
  slug: SlugSchema,
  organizerId: UuidSchema,
  name: z.string(),
  description: z.string(),
  status: OrganizationStatusSchema,
  requirements: RequirementsV2Schema,
  /** Non-blocking warnings about the requirements (R15), e.g. "only IP accepted for a small area". */
  requirementsWarnings: z.array(RequirementsWarningSchema),
  districts: DistrictsSchema,
  testMode: z.boolean(),
  testOutcome: TestOutcomeSchema,
  testMix: TestMixSchema,
  /** `${PUBLIC_URL}/o/${slug}` (landing). */
  publicUrl: z.string(),
  /** `${PUBLIC_URL}/o/${slug}/join` (share link + QR). */
  joinUrl: z.string(),
  diditWorkflowId: z.string().nullable(),
  /** Last Didit rejection of the workflow at registration time (registrations fail until fixed). */
  diditWorkflowError: DiditRejectionSchema.extend({ at: IsoDateTimeSchema }).nullable(),
  /**
   * The organization's creator wallet: the account that creates its votings on chain, and the one an election
   * committee authorizes. Derived server-side from the deployment's master seed (never stored as a key).
   */
  creatorAddress: EthAddressSchema.nullable(),
  /** Fields fixed after publishing. */
  locks: z.object({ slug: z.boolean(), testMode: z.boolean() }),
  actions: OrganizationActionsSchema,
  publishedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
})
export type Organization = z.infer<typeof OrganizationSchema>

export const OrganizationResponseSchema = z.object({ organization: OrganizationSchema })
export type OrganizationResponse = z.infer<typeof OrganizationResponseSchema>

/* GET /api/org/organizations → OrganizationListResponse (the organizer's organizations, newest first). */
export const OrganizationSummarySchema = z.object({
  id: UuidSchema,
  slug: SlugSchema,
  name: z.string(),
  status: OrganizationStatusSchema,
  testMode: z.boolean(),
  publicUrl: z.string(),
  counts: z.object({
    /** Non-revoked member rows. */
    total: z.number().int().min(0),
    active: z.number().int().min(0),
    /** registered + kyc_pending + approved (no account yet) */
    pending: z.number().int().min(0),
    inReview: z.number().int().min(0),
    removed: z.number().int().min(0),
  }),
  elections: z.object({ total: z.number().int().min(0), open: z.number().int().min(0) }),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
})
export type OrganizationSummary = z.infer<typeof OrganizationSummarySchema>

export const OrganizationListResponseSchema = z.object({ organizations: z.array(OrganizationSummarySchema) })
export type OrganizationListResponse = z.infer<typeof OrganizationListResponseSchema>

/* GET /api/org/organizations/slug-available?slug= → SlugAvailabilityResponse (wizard hint; create re-checks). */
export const SlugAvailabilityQuerySchema = z.object({ slug: SlugSchema })
export type SlugAvailabilityQuery = z.infer<typeof SlugAvailabilityQuerySchema>
export const SlugAvailabilityResponseSchema = z.object({ slug: z.string(), available: z.boolean() })
export type SlugAvailabilityResponse = z.infer<typeof SlugAvailabilityResponseSchema>

/*
 * POST /api/org/organizations/:orgId/publish → OrganizationResponse (draft → published; creates the Didit workflow
 * in the organization's app: 422 didit_config_rejected and the organization stays a draft; Didit unreachable ⇒
 * published anyway, the workflow is created at the first registration). 400 validation_error with the publish
 * issues; 503 didit_unavailable for a test organization on a server without a sandbox key.
 * DELETE /api/org/organizations/:orgId → OkResponse (draft without votings only, else 409 invalid_state_transition).
 */

/* ================================================================== dashboard (Overview tab) */

export const MemberStatusCountsSchema = z.record(MemberStatusSchema, z.number().int().min(0))
export type MemberStatusCounts = z.infer<typeof MemberStatusCountsSchema>

/* GET /api/org/organizations/:orgId/dashboard → OrganizationDashboardResponse */
export const OrganizationDashboardResponseSchema = z.object({
  organization: OrganizationSchema,
  kpis: z.object({
    /** Count per member status (every status key present, 0 when none; revoked rows included). */
    byStatus: MemberStatusCountsSchema,
    /** Non-revoked member rows. */
    totalMembers: z.number().int().min(0),
    kycSessions: z.number().int().min(0),
    accountsCreated: z.number().int().min(0),
    /** in_review + members with open review reasons. */
    needsReview: z.number().int().min(0),
    districtUnresolved: z.number().int().min(0),
    /** Active members who opted out of emails. */
    emailOptOut: z.number().int().min(0),
  }),
  /** Registrations per day (UTC), oldest first. */
  registrationsByDay: z.array(z.object({ day: z.string(), count: z.number().int().min(0) })),
  /** Non-revoked members per district (only when the organization has a districting list). */
  byDistrict: z.array(z.object({ district: z.string().nullable(), count: z.number().int().min(0) })),
  elections: z.object({
    /** Count per voting status (every status key present). */
    byStatus: z.record(ElectionStatusSchema, z.number().int().min(0)),
    /** Up to 5 votings: open first, then upcoming, then the latest. */
    highlighted: z.array(ElectionSummarySchema),
  }),
  /** Email delivery of this deployment (UI warning when not configured). */
  email: EmailStatusSchema,
  diditMode: DiditModeSchema,
})
export type OrganizationDashboardResponse = z.infer<typeof OrganizationDashboardResponseSchema>
